import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Icon, IconButton, SkeletonText } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import "./channel-search.css";

/**
 * Finding something said in this conversation.
 *
 * Scoped to the open channel, because that is the question people actually
 * have — "where did we agree that" — and because a search that ranged wider
 * would have to answer for every private channel the asker is not in.
 *
 * Results are a way back to the message, not a replacement for reading it:
 * clicking one scrolls the thread to that message and highlights it, rather
 * than opening a separate reader.
 */

export interface SearchResult {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  createdAt: string;
}

export interface ChannelSearchProps {
  channelId: string;
  onClose: () => void;
  onJump: (messageId: string) => void;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
  });
}

export function ChannelSearch({ channelId, onClose, onJump }: ChannelSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Opening a search box that is not focused makes somebody click it, which is
  // a step the shortcut was meant to remove.
  useEffect(() => inputRef.current?.focus(), []);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const run = useCallback(
    (text: string) => {
      // Two characters is the server's floor as well; one letter matches so
      // much that the result list is noise.
      if (text.trim().length < 2) {
        setResults(null);
        setError(null);
        return;
      }
      setLoading(true);
      api
        .get<{ results: SearchResult[] }>(
          `/channels/${channelId}/messages/search?q=${encodeURIComponent(text.trim())}`,
        )
        .then((res) => {
          setResults(res.results);
          setError(null);
        })
        .catch((err: unknown) => {
          setError(err instanceof ApiError ? err.message : "Could not search this conversation.");
          setResults(null);
        })
        .finally(() => setLoading(false));
    },
    [channelId],
  );

  return (
    <div className="channel-search">
      <div className="channel-search__bar">
        <Icon name="search" size={14} />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Search this conversation…"
          aria-label="Search this conversation"
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => run(next), 250);
          }}
        />
        <IconButton icon={<Icon name="close" size={14} />} label="Close search" size="sm" onClick={onClose} />
      </div>

      <div className="channel-search__results">
        {loading && !results ? (
          <div className="channel-search__loading">
            <SkeletonText lines={3} />
          </div>
        ) : error ? (
          <p className="channel-search__empty">{error}</p>
        ) : results === null ? (
          <p className="channel-search__empty">Type at least two characters.</p>
        ) : results.length === 0 ? (
          <p className="channel-search__empty">Nothing in this conversation matches “{query.trim()}”.</p>
        ) : (
          <>
            <p className="channel-search__count">
              {results.length} {results.length === 1 ? "message" : "messages"}
            </p>
            {results.map((result) => (
              <button
                key={result.id}
                type="button"
                className="channel-search__hit"
                onClick={() => onJump(result.id)}
              >
                <Avatar name={result.authorName} src={result.authorAvatarUrl ?? undefined} size={22} />
                <span className="channel-search__hit-body">
                  <span className="channel-search__hit-meta">
                    <strong>{result.authorName}</strong>
                    <span>{formatWhen(result.createdAt)}</span>
                  </span>
                  <span className="channel-search__hit-text">{result.body}</span>
                </span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
